import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaSearch, FaExclamationTriangle, FaTelegram, FaExclamationCircle, FaEye, FaServer, FaSync } from 'react-icons/fa';
import HolyLogo from '../components/HolyLogo';
import { scammerService } from '../services/api';

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-15px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2.5rem;
  animation: ${fadeIn} 0.6s ease-out;
`;

const PageHeader = styled.div`
  margin-bottom: 3rem;
  position: relative;
`;

const Title = styled.h1`
  font-size: 2.2rem;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.75rem;
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    width: 60px;
    height: 4px;
    background: ${props => props.theme.colors.gradient1};
    bottom: -10px;
    left: 0;
    border-radius: 2px;
  }
`;

const Subtitle = styled.p`
  color: ${props => props.theme.colors.textMuted};
  margin-bottom: 2rem;
  line-height: 1.6;
  max-width: 700px;
  font-size: 1.1rem;
`;

const SearchContainer = styled.div`
  position: relative;
  max-width: 850px;
  margin: 0 auto 3.5rem;
  width: 100%;
  animation: ${fadeIn} 0.7s ease-out;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 1.3rem 1.5rem 1.3rem 3.5rem;
  font-size: 1.05rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  background-color: rgba(15, 15, 20, 0.5);
  color: ${props => props.theme.colors.text};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  backdrop-filter: blur(5px);
  
  &:focus {
    outline: none;
    border-color: rgba(108, 92, 231, 0.3);
    box-shadow: 0 0 0 3px rgba(108, 92, 231, 0.2), 0 4px 15px rgba(0, 0, 0, 0.05);
    background-color: rgba(20, 20, 25, 0.7);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const SearchStats = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.95rem;
  color: ${props => props.theme.colors.textMuted};
  margin: 1rem 0.5rem 0;
  
  @media (max-width: 600px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const ScammerList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

const scamCardAnimation = keyframes`
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ScammerCard = styled.div`
  background: rgba(26, 26, 26, 0.5);
  border-radius: 16px;
  overflow: hidden;
  animation: ${scamCardAnimation} 0.5s ease-out forwards;
  animation-delay: var(--delay, 0);
  opacity: 0;
  box-shadow: ${props => props.theme.boxShadow.medium};
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(5px);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${props => props.theme.boxShadow.large};
  }
`;

const ScammerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.2rem 1.5rem;
  background: linear-gradient(90deg, rgba(108, 92, 231, 0.15), rgba(108, 92, 231, 0.05));
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
`;

const ScammerInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const ScammerUsername = styled.div`
  font-size: 1.15rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${props => props.theme.colors.primary};

  svg {
    color: #0088cc;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2));
  }
`;

const ScammerBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background-color: rgba(231, 76, 60, 0.15);
  color: ${props => props.theme.colors.error};
  font-size: 0.85rem;
  font-weight: 500;
  padding: 0.4rem 0.8rem;
  border-radius: 6px;
  white-space: nowrap;
  
  svg {
    font-size: 0.8rem;
  }
`;

const ScammerReason = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
`;

const ReasonLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: ${props => props.theme.colors.text};
  font-size: 0.95rem;
  
  svg {
    color: ${props => props.theme.colors.primary};
  }
`;

const ReasonText = styled.p`
  color: ${props => props.theme.colors.textMuted};
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0;
`;

const EvidenceContainer = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
`;

const EvidenceLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  
  span {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 600;
    color: ${props => props.theme.colors.text};
    font-size: 0.95rem;
    
    svg {
      color: ${props => props.theme.colors.primary};
    }
  }
`;

const EvidenceImages = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
`;

const EvidenceImage = styled.div`
  position: relative;
  aspect-ratio: 1;
  overflow: hidden;
  border-radius: 8px;
  cursor: pointer;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.3);
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: 1;
  }
  
  &:hover {
    &::before {
      opacity: 1;
    }
    
    svg {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const ViewIcon = styled(FaEye)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(0.8);
  color: white;
  font-size: 1.2rem;
  z-index: 2;
  opacity: 0;
  transition: all 0.3s ease;
`;

const Divider = styled.hr`
  border: none;
  height: 1px;
  background-color: rgba(255, 255, 255, 0.05);
  margin: 0;
`;

const TelegramButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  background: #0088cc;
  color: white;
  font-weight: 600;
  padding: 0.8rem;
  margin: 1.2rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    background: #0077b5;
    color: white;
    transform: translateY(-2px);
  }
  
  svg {
    font-size: 1.2rem;
  }
`;

const ScammerDate = styled.div`
  padding: 0 1.5rem 1.5rem;
  color: ${props => props.theme.colors.textMuted};
  font-size: 0.85rem;
  text-align: right;
`;

const loadingAnimation = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 5rem 2rem;
  color: ${props => props.theme.colors.textMuted};
  height: 40vh;
  
  div {
    width: 60px;
    height: 60px;
    border: 3px solid rgba(108, 92, 231, 0.1);
    border-radius: 50%;
    border-top-color: ${props => props.theme.colors.primary};
    animation: ${loadingAnimation} 1s ease-in-out infinite;
    margin-bottom: 2rem;
  }
  
  p {
    font-size: 1.3rem;
    font-weight: 500;
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 5rem 2rem;
  color: ${props => props.theme.colors.error};
  background-color: rgba(20, 20, 25, 0.3);
  border-radius: 16px;
  margin: 3rem auto;
  max-width: 700px;
  box-shadow: ${props => props.theme.boxShadow.small};
  backdrop-filter: blur(5px);
  border: 1px solid rgba(231, 76, 60, 0.1);
  
  svg {
    font-size: 4rem;
    margin-bottom: 2rem;
    opacity: 0.8;
  }
  
  p {
    font-size: 1.3rem;
    text-align: center;
    max-width: 500px;
    line-height: 1.6;
  }
`;

const NoResults = styled(ErrorContainer)`
  color: ${props => props.theme.colors.textMuted};
  background-color: rgba(20, 20, 25, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.05);
`;

const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${props => props.theme.colors.primary};
  color: white;
  font-weight: 600;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  margin-left: 1rem;
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 8px rgba(108, 92, 231, 0.2);
  
  &:hover {
    background: ${props => props.theme.colors.primaryLight};
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(108, 92, 231, 0.3);
  }
  
  svg {
    animation: ${props => props.isRefreshing ? spin : 'none'} 1s linear infinite;
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  margin-left: 1rem;
  background-color: ${props => props.status === 'error' ? 'rgba(231, 76, 60, 0.15)' : 'rgba(37, 176, 129, 0.15)'};
  color: ${props => props.status === 'error' ? 'rgb(231, 76, 60)' : 'rgb(37, 176, 129)'};
  border: 1px solid ${props => props.status === 'error' ? 'rgba(231, 76, 60, 0.3)' : 'rgba(37, 176, 129, 0.3)'};
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  
  svg {
    margin-right: 0.4rem;
  }
`;

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' });
};

const ScamList = () => {
  const [scammers, setScammers] = useState([]);
  const [filteredScammers, setFilteredScammers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const fetchScammers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching scammers data from API...');
      const data = await scammerService.getAll();
      
      setScammers(data);
      setFilteredScammers(data);
      console.log(`Successfully loaded ${data.length} scammers from API`);
    } catch (err) {
      console.error('Error loading scammers:', err);
      setError('Impossible de charger les données. Veuillez réessayer plus tard.');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchScammers();
  }, []);
  
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await fetchScammers();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    
    if (!query.trim()) {
      setFilteredScammers(scammers);
      return;
    }
    
    const filtered = scammers.filter(scammer => {
      return (
        (scammer.telegramUsername && scammer.telegramUsername.toLowerCase().includes(query)) ||
        (scammer.telegramId && scammer.telegramId.toString().includes(query)) ||
        (scammer.reason && scammer.reason.toLowerCase().includes(query))
      );
    });
    
    setFilteredScammers(filtered);
  };

  return (
    <PageContainer>
      <PageHeader>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
          <Title>Liste des Scammers</Title>
          <StatusBadge status={error ? 'error' : 'success'}>
            <FaServer /> {error ? 'Erreur de connexion' : 'API Connectée'}
          </StatusBadge>
          <RefreshButton 
            onClick={handleRefresh} 
            disabled={isRefreshing || loading}
            isRefreshing={isRefreshing}
            aria-label="Actualiser les données"
          >
            <FaSync /> {isRefreshing ? 'Actualisation...' : 'Actualiser'}
          </RefreshButton>
        </div>
        <Subtitle>
          Consultez la liste des scammers vérifiés et signalés par notre communauté.
        </Subtitle>
      </PageHeader>

      <SearchContainer>
        <div style={{ 
          position: 'absolute', 
          left: '1.5rem', 
          top: '1.3rem',
          zIndex: 5,
          pointerEvents: 'none' 
        }}>
          <FaSearch 
            size="1.1rem" 
            color="#6c5ce7"
            opacity={0.7}
            aria-hidden="true"
          />
        </div>
        <SearchInput
          type="text"
          placeholder="Rechercher par username Telegram ou description..." 
          value={searchQuery}
          onChange={handleSearchChange}
          disabled={loading || error}
          aria-label="Rechercher des scammers"
        />
        
        {scammers.length > 0 && (
          <SearchStats>
            <span>
              {filteredScammers.length} scammer{filteredScammers.length !== 1 ? 's' : ''} trouvé{filteredScammers.length !== 1 ? 's' : ''}
              {searchQuery && ` pour "${searchQuery}"`}
            </span>
            <span>
              Total: {scammers.length} scammer{scammers.length !== 1 ? 's' : ''} dans la base de données
            </span>
          </SearchStats>
        )}
      </SearchContainer>

      {loading ? (
        <LoadingContainer>
          <div></div>
          <p>Chargement en cours...</p>
        </LoadingContainer>
      ) : error ? (
        <ErrorContainer>
          <FaExclamationTriangle />
          <p>{error}</p>
          <RefreshButton 
            onClick={handleRefresh}
            disabled={isRefreshing}
            isRefreshing={isRefreshing}
          >
            <FaSync /> {isRefreshing ? 'Actualisation...' : 'Réessayer'}
          </RefreshButton>
        </ErrorContainer>
      ) : filteredScammers.length === 0 ? (
        <NoResults>
          <FaExclamationTriangle />
          <p>{searchQuery ? 'Aucun scammer ne correspond à votre recherche. Essayez un autre terme ou vérifiez l\'orthographe.' : 'Aucun scammer dans la base de données.'}</p>
        </NoResults>
      ) : (
        <ScammerList>
          {filteredScammers.map((scammer, index) => (
            <ScammerCard key={scammer._id} style={{ '--delay': `${index * 0.08}s` }}>
              <ScammerHeader>
                <ScammerInfo>
                  <ScammerUsername>
                    <FaTelegram /> @{scammer.telegramUsername}
                  </ScammerUsername>
                </ScammerInfo>
                <ScammerBadge>
                  <FaExclamationTriangle /> Scammer
                </ScammerBadge>
              </ScammerHeader>
              
              <ScammerReason>
                <ReasonLabel>
                  <FaExclamationCircle /> Raison du signalement
                </ReasonLabel>
                <ReasonText>{scammer.reason}</ReasonText>
              </ScammerReason>
              
              {scammer.evidenceImages && scammer.evidenceImages.length > 0 && (
                <EvidenceContainer>
                  <EvidenceLabel>
                    <span>
                      <HolyLogo style={{ marginRight: '0.3rem', fontSize: '1rem' }} /> Preuves
                    </span>
                  </EvidenceLabel>
                  <EvidenceImages>
                    {scammer.evidenceImages.map((image, idx) => (
                      <EvidenceImage 
                        key={idx} 
                        onClick={() => window.open(image, '_blank')}
                      >
                        <img src={image} alt={`Preuve ${idx + 1}`} />
                        <ViewIcon />
                      </EvidenceImage>
                    ))}
                  </EvidenceImages>
                </EvidenceContainer>
              )}
              
              <Divider />
              
              <TelegramButton 
                href={`https://t.me/${scammer.telegramUsername}`} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <FaTelegram /> Voir sur Telegram
              </TelegramButton>
              
              <ScammerDate>Signalé le {formatDate(scammer.createdAt)}</ScammerDate>
            </ScammerCard>
          ))}
        </ScammerList>
      )}
    </PageContainer>
  );
};

export default ScamList; 